import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import prayerTimesService from '../services/prayerTimesService';
import { DEFAULT_LOCATION } from '../utils/constants';

// Create the context
const PrayerTimesContext = createContext(null);

/**
 * Provider component for prayer times data and functionality
 */
export const PrayerTimesProvider = ({ children }) => {
    // State for prayer times data
    const [prayerTimes, setPrayerTimes] = useState(null);
    const [hijriDate, setHijriDate] = useState(null);
    const [methodInfo, setMethodInfo] = useState(null);

    // UI state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Location detection state
    const [locationRequested, setLocationRequested] = useState(false);

    // Reference for tracking initial load
    const initialLoadRef = useRef(false);

    // Calculation parameters - update to use numeric values for MUI compatibility
    const [params, setParams] = useState({
        latitude: DEFAULT_LOCATION.latitude,
        longitude: DEFAULT_LOCATION.longitude,
        address: DEFAULT_LOCATION.address,
        method: 2, // ISNA by default
        school: 0, // Standard juristic method
        latitudeAdjustmentMethod: 3, // ANGLE_BASED as numeric value
        midnightMode: 0, // STANDARD as numeric value
        timezone: null
    });

    /**
     * For API calls, convert numeric values to strings
     * This doesn't need to be recreated on every params change since we
     * directly access the current params value when it's called
     */
    const getApiParams = useCallback(() => {
        const apiParams = { ...params };

        // Convert numeric values to strings for API
        if (typeof apiParams.latitudeAdjustmentMethod === 'number') {
            const methods = ['NONE', 'NIGHT_MIDDLE', 'ONE_SEVENTH', 'ANGLE_BASED'];
            apiParams.latitudeAdjustmentMethod = methods[apiParams.latitudeAdjustmentMethod] || 'ANGLE_BASED';
        }

        if (typeof apiParams.midnightMode === 'number') {
            const modes = ['STANDARD', 'JAFARI'];
            apiParams.midnightMode = modes[apiParams.midnightMode] || 'STANDARD';
        }

        return apiParams;
    }, []); // Empty dependency array - we'll access current params at call time

    /**
     * Update calculation parameters - this is stable and doesn't need to be recreated
     */
    const updateParams = useCallback((newParams) => {
        setParams(prevParams => ({ ...prevParams, ...newParams }));
    }, []);

    /**
     * Fetch prayer times using coordinates
     * We removed the circular dependencies by leveraging function closure
     */
    const fetchPrayerTimesByCoordinates = useCallback(async (customParams = {}) => {
        // Prevent duplicate location requests or unnecessary fetches
        if (loading) return;

        setLoading(true);
        setError(null);
        setLocationRequested(true);

        try {
            // Get API-friendly params
            const baseParams = getApiParams();

            // Merge with custom params
            const queryParams = {
                ...baseParams,
                ...customParams
            };

            // Format date if provided
            if (queryParams.date instanceof Date) {
                queryParams.date = queryParams.date.toISOString().split('T')[0];
            }

            const response = await prayerTimesService.getTimingsByCoordinates(queryParams);

            if (response.code === 200) {
                setPrayerTimes(response.data.timings);
                setHijriDate(response.data.date.hijri);
                setMethodInfo(response.data.meta.method);

                // Update timezone if provided in the response
                if (response.data.meta.timezone && response.data.meta.timezone !== params.timezone) {
                    updateParams({ timezone: response.data.meta.timezone });
                }
            } else {
                throw new Error(response.status || 'Failed to fetch prayer times');
            }
        } catch (err) {
            console.error('Error fetching prayer times:', err);
            setError(err.message || 'An error occurred while fetching prayer times');
        } finally {
            setLoading(false);
        }
    }, []); // Empty dependency array to prevent recreation

    /**
     * Fetch prayer times using an address
     * We removed the circular dependencies by leveraging function closure
     */
    const fetchPrayerTimesByAddress = useCallback(async (address, customParams = {}) => {
        // Prevent duplicate location requests
        if (loading && !locationRequested) return;

        setLoading(true);
        setError(null);
        setLocationRequested(true);

        try {
            // Get API-friendly params
            const baseParams = getApiParams();

            // Merge with custom params
            const queryParams = {
                ...baseParams,
                ...customParams,
                address
            };

            // Format date if provided
            if (queryParams.date instanceof Date) {
                queryParams.date = queryParams.date.toISOString().split('T')[0];
            }

            const response = await prayerTimesService.getTimingsByAddress(queryParams);

            if (response.code === 200) {
                setPrayerTimes(response.data.timings);
                setHijriDate(response.data.date.hijri);
                setMethodInfo(response.data.meta.method);

                // Update coordinates if they're in the response
                if (response.data.meta.latitude !== undefined && response.data.meta.longitude !== undefined) {
                    updateParams({
                        latitude: response.data.meta.latitude,
                        longitude: response.data.meta.longitude,
                        address,
                        timezone: response.data.meta.timezone
                    });
                }
            } else {
                throw new Error(response.status || 'Failed to fetch prayer times');
            }
        } catch (err) {
            console.error('Error fetching prayer times by address:', err);
            setError(err.message || 'An error occurred while fetching prayer times');
        } finally {
            setLoading(false);
        }
    }, []); // Empty dependency array

    /**
     * Reset all prayer times data
     */
    const resetPrayerTimes = useCallback(() => {
        setPrayerTimes(null);
        setHijriDate(null);
        setMethodInfo(null);
        setError(null);
        setLocationRequested(false);
    }, []);

    // Load default prayer times on first mount only
    useEffect(() => {
        // Only run this once on initial mount
        if (!initialLoadRef.current) {
            initialLoadRef.current = true;

            if (!prayerTimes && !loading && !locationRequested) {
                fetchPrayerTimesByCoordinates();
            }
        }
    }, []); // Empty dependency array = run once on mount

    // Create the context value object
    const value = {
        // Data
        prayerTimes,
        hijriDate,
        methodInfo,
        params,

        // Status
        loading,
        error,
        locationRequested,

        // Actions
        fetchPrayerTimesByCoordinates,
        fetchPrayerTimesByAddress,
        updateParams,
        resetPrayerTimes
    };

    // Provide the context to children
    return (
        <PrayerTimesContext.Provider value={value}>
            {children}
        </PrayerTimesContext.Provider>
    );
};

/**
 * Custom hook for using the prayer times context
 */
export const usePrayerTimes = () => {
    const context = useContext(PrayerTimesContext);

    if (!context) {
        throw new Error('usePrayerTimes must be used within a PrayerTimesProvider');
    }

    return context;
};

export default PrayerTimesContext;